import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DataTypeOptions } from '@core/constants/data-types.constants';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { CurrencyExchange, convert } from '@core/models/currency-exchange';
import { InsuranceTarget } from '@core/models/insurance-target.model';
import { ProductCoverage } from '@core/models/product-coverage.model';
import { Product } from '@core/models/product.model';
import { LiteService } from '@core/services/lite.service';
import { LanguageService } from '@i18n/services/language.service';
import { getFieldError, isFieldInvalid, isFieldValid } from '@shared/utils/forms/validations.util';

@Component({
  selector: 'mfp-product-feature-multiple-coverages',
  templateUrl: 'product-feature-multiple-coverages.component.html',
  styleUrls: ['./product-feature-multiple-coverages.component.scss']
})
export class ProductFeatureMultipleCoveragesComponent implements OnInit {
  @Input() coverages!: ProductCoverage[];
  @Input() selectedCoverage!: ProductCoverage;
  @Input() coverageGroup!: string;
  @Input() insuranceTarget!: InsuranceTarget;
  @Input() description!: string;
  @Input() product!: Product;
  @Input() currencyExchange!: CurrencyExchange;
  @Input() disabled = true;
  @Input() placeholder = '';
  @Output() coverageChange = new EventEmitter<ProductCoverage>();

  isFieldValid = isFieldValid;
  isFieldInvalid = isFieldInvalid;
  getFieldError = getFieldError;

  values: DataTypeOptions<string> = [];

  constructor(private language: LanguageService, private liteService: LiteService) {}

  ngOnInit() {
    this.coverages.forEach(coverage => {
      this.values.push({
        key: coverage.description,
        value: coverage.id
      });
    });
  }

  onChangeSelect(event: string) {
    this.selectedCoverage.active = false;
    this.coverageChange.emit(this.selectedCoverage);

    this.selectedCoverage = this.coverages.filter(value => {
      return value.id === event;
    })[0];

    this.selectedCoverage.active = true;

    if (this.getAmountError() !== '' || this.getSelectError() !== '') {
      this.selectedCoverage.valid = false;
    } else {
      this.selectedCoverage.valid = true;
    }

    this.coverageChange.emit(this.selectedCoverage);
  }

  getValue() {
    const amount = this.getAmount();
    switch (this.selectedCoverage.valueType) {
      case 'number':
        return amount;
      case 'percentage':
        let percentage = 0;
        switch (this.product.type) {
          case 'normal':
            const currentAssuredAmount = convert(
              this.product.currentAssuredAmount,
              this.product.selectedCurrency,
              this.currencyExchange
            );
            percentage = (amount / currentAssuredAmount.amount) * 100;
            break;
          case 'investment':
            const amountAtEndOfTemporality = convert(
              this.product.amountAtEndOfTemporality,
              this.product.selectedCurrency,
              this.currencyExchange
            );
            percentage = (amount / amountAtEndOfTemporality.amount) * 100;
            break;
        }
        return `${Math.round(percentage * 100) / 100}%`;
    }
  }

  getAmount() {
    if (this.selectedCoverage.currentAmount) {
      return Math.round(
        convert(this.selectedCoverage.currentAmount, this.product.selectedCurrency, this.currencyExchange).amount
      );
    }
    return 0;
  }

  getMin() {
    if (this.selectedCoverage.amountLimits?.min.amount) {
      const currencyAmount: CurrencyAmount = {
        amount: this.selectedCoverage.amountLimits?.min.amount,
        currency: this.selectedCoverage.amountLimits?.min.currency
      };
      return Math.round(convert(currencyAmount, this.product.selectedCurrency, this.currencyExchange).amount);
    }
    return 0;
  }

  getMax() {
    if (this.selectedCoverage.amountLimits?.max.amount) {
      const currencyAmount: CurrencyAmount = {
        amount: this.selectedCoverage.amountLimits?.max.amount,
        currency: this.selectedCoverage.amountLimits?.max.currency
      };
      return Math.round(convert(currencyAmount, this.product.selectedCurrency, this.currencyExchange).amount);
    }
    return 0;
  }

  getSelectValue() {
    return this.selectedCoverage.id;
  }

  onChangeActive(active: boolean) {
    this.selectedCoverage.active = active;

    if (this.getAmountError() !== '' || this.getSelectError() !== '' || !this.selectedCoverage.currentAmount) {
      this.selectedCoverage.valid = false;
    } else {
      this.selectedCoverage.valid = true;
    }

    this.coverageChange.emit(this.selectedCoverage);
  }

  onChangeAmount(event: string) {
    if (isNaN(+event)) {
      event = event.replace('%', '');
      if (isNaN(+event)) {
        return;
      }
      switch (this.product.type) {
        case 'normal':
          const currentAssuredAmount = convert(
            this.product.currentAssuredAmount,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * currentAssuredAmount.amount + '';
          break;
        case 'investment':
          const amountAtEndOfTemporality = convert(
            this.product.amountAtEndOfTemporality,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * amountAtEndOfTemporality.amount + '';
          break;
      }
    }
    const amount = +event;
    if (amount) {
      this.selectedCoverage.currentAmount = {
        currency: this.product.selectedCurrency,
        amount
      };
    } else {
      delete this.selectedCoverage.currentAmount;
    }

    if (this.getAmountError() !== '' || this.getSelectError() !== '') {
      this.selectedCoverage.valid = false;
    } else {
      this.selectedCoverage.valid = true;
    }

    this.liteService.coverageValueInsuredAmount.emit(false);
    this.coverageChange.emit(this.selectedCoverage);
  }

  getAmountError(): string {
    if (!this.selectedCoverage.currentAmount) {
      return this.language.instant('PRODUCT_SELECT.MANDATORY_FIELD');
    }
    const amount = this.getAmount();
    if (this.selectedCoverage.amountLimits?.min.amount || this.selectedCoverage.amountLimits?.min.percentage) {
      const min = this.getMin();
      if (amount < min) {
        let percentage;
        if (this.selectedCoverage.percentageLimits?.min.percentage) {
          percentage = Math.round(this.selectedCoverage.percentageLimits?.min.percentage * 100) / 100;
        }
        return this.language.instant('PRODUCT_SELECT.COVERAGE.ERROR.MIN', {
          min: this.selectedCoverage.valueType === 'number' ? min : `${percentage}%`
        });
      }
    }
    if (this.selectedCoverage.amountLimits?.max.amount || this.selectedCoverage.amountLimits?.max.percentage) {
      const max = this.getMax();
      if (amount > max) {
        let percentage;
        if (this.selectedCoverage.percentageLimits?.max.percentage) {
          percentage = Math.round(this.selectedCoverage.percentageLimits?.max.percentage * 100) / 100;
        }
        return this.language.instant('PRODUCT_SELECT.COVERAGE.ERROR.MAX', {
          max: this.selectedCoverage.valueType === 'number' ? max : `${percentage}%`
        });
      }
    }
    return '';
  }

  getSelectError(): string {
    if (!this.selectedCoverage) {
      return this.language.instant('PRODUCT_SELECT.MANDATORY_FIELD');
    }
    return '';
  }
}
